/**
 * @file    App.jsx - Renders the Root of the portal app.
 *
 * @author  Alex Huctwith <alex@distributive.network>
 * @date    April 2022
 */

import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AccountsDispatchContext } from '@/features/accounts';
import { useUserPrefUpdate } from '@/features/user';
import { AccountOperations } from '@/features/accounts';
import { UserOperations } from '@/features/user';
import { AppRoutes } from '@/routes';

/**
 * Renders the app.
 *
 * @returns {JSX.Element} The entire app component.
 */
function App()
{
  const [isAccountsLoading, setAccountsLoading] = useState(true);
  const { handleRefreshAccounts } = useContext(AccountsDispatchContext);
  const setUserPreferences = useUserPrefUpdate();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchUserPreferences()
    {
      await dcp.identity.waitForSession();
      setUserPreferences({ showWarnModalOnLogin: true });
      const userPreferences = await UserOperations.getUserPreferences();
    }

    fetchUserPreferences();
  }, [setUserPreferences]);

  useEffect(() => {
    /**
     * Side effect after initially rendering the app: Fetch the accounts the
     * user has from the server.
     */
    async function fetchAccounts()
    {
      const refreshedAccounts = await AccountOperations.refreshKeystores();
      handleRefreshAccounts(refreshedAccounts);
      setAccountsLoading(false);
    }

    fetchAccounts();
  }, [handleRefreshAccounts]);
  
  useEffect(() => {
    if (window.location.pathname !== '/' && window.location.pathname !== '/auth/callback')
      localStorage.setItem('latestPage', window.location.pathname);
  }, [navigate]);

  return isAccountsLoading ? (
    <div className="loader-container"></div>
  ) : (
    <AppRoutes />
  );
}

export default App;
